import * as React from 'react';
import { inject, observer } from 'mobx-react';
import moment from 'moment-timezone';
import { FaDownload, FaEllipsisV, FaSearch, FaTimes } from 'react-icons/fa';
import { FormGroup, RotateLoader } from '@lib/components';
import { MobxComponent } from '../../../../../mobx/components';
import { UI } from '../../../../../core/ui';
import {
  LC,
  LoyaltyButton,
  LoyaltyCard,
  LoyaltyCardHeading,
  LoyaltyShell,
} from './shell';
import { nativeLoyaltyGiftCardMinor, nativeLoyaltyMemberPoints } from './helpers';
import { CoreUtils } from '@lib/common';

interface Props {}
interface State {
  loading: boolean;
  error: string;
  query: string;
  members: any[];
  page: number;
  total: number;
  selected: any;
  detailLoading: boolean;
  actionError: string;
  points: string;
  reason: string;
  giftAmount: string;
  action: 'add' | 'subtract';
  detailModalOpen: boolean;
  detailTab: 'points' | 'gift_card';
}

const COLUMNS = [
  { key: 'firstName', title: 'First Name', width: 110 },
  { key: 'lastName',  title: 'Last Name',  width: 110 },
  { key: 'email',     title: 'Email' },
  { key: 'phone',     title: 'Phone',      width: 130 },
  { key: 'cardId',    title: 'Card ID',    width: 110 },
  { key: 'birthDate', title: 'Birthdate',  width: 100 },
  { key: 'createdAt', title: 'Created',    width: 100 },
  { key: 'points',    title: 'Points',     width: 80, align: 'right' as const },
  { key: 'status',    title: 'Status',     width: 100, align: 'center' as const },
];

const PAGE_SIZE = 20;

@inject('store')
@observer
export class RestaurantLoyaltyMembers extends MobxComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      loading: true,
      error: '',
      query: '',
      members: [],
      page: 1,
      total: 0,
      selected: null,
      detailLoading: false,
      actionError: '',
      points: '',
      reason: '',
      giftAmount: '',
      action: 'add',
      detailModalOpen: false,
      detailTab: 'points',
    };
  }

  componentDidMount() {
    this.search();
  }

  canAdjust() {
    return this.injected.store.restrictions.restaurant.loyalty_member_balances === true;
  }

  search = async (page = 1) => {
    const { store } = this.injected;
    const restaurant = store.restaurant!;
    this.setState({ loading: true, error: '', page });
    try {
      const response = await store.api.native_loyalty_members({
        restaurant_id: restaurant._id,
        query: this.state.query,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      });
      if (response.outcome) {
        this.setState({ loading: false, error: response.message || 'Failed to load members' });
        return;
      }
      const rows = response.data?.customers || [];
      const total = response.data?.total ?? rows.length;
      this.setState({ loading: false, members: Array.isArray(rows) ? rows : [], total });
    } catch (e) {
      this.setState({ loading: false, error: 'Failed to load members' });
    }
  };

  openMember = async (customerId: string) => {
    const { store } = this.injected;
    const restaurant = store.restaurant!;
    this.setState({
      detailModalOpen: true,
      detailLoading: true,
      actionError: '',
      selected: null,
      points: '',
      reason: '',
      giftAmount: '',
    });
    try {
      const response = await store.api.native_loyalty_member_detail({
        restaurant_id: restaurant._id,
        customer_id: customerId,
      });
      if (response.outcome) {
        this.setState({
          detailLoading: false,
          actionError: response.message || 'Failed to load member',
        });
        return;
      }
      this.setState({ detailLoading: false, selected: response.data });
    } catch (e) {
      this.setState({ detailLoading: false, actionError: 'Failed to load member' });
    }
  };

  closeMemberModal = () => {
    this.setState({
      detailModalOpen: false,
      selected: null,
      detailLoading: false,
      actionError: '',
      points: '',
      reason: '',
      giftAmount: '',
      detailTab: 'points',
    });
  };

  memberModalTitle(selected: any): string {
    const customer = selected?.customer || selected;
    if (!customer) return 'Member detail';
    const name = String(
      customer.name ||
        customer.full_name ||
        [customer.first_name || customer.firstName, customer.last_name || customer.lastName]
          .filter(Boolean)
          .join(' ')
    ).trim();
    return name || customer.email || 'Member detail';
  }

  memberCustomerId(selected: any): string {
    return selected?.customer?.id || selected?.id;
  }

  currentPoints(selected: any): number {
    const locationId = this.injected.store.restaurant?.settings?.loyalty_providers?.native?.location_id || undefined;
    return nativeLoyaltyMemberPoints(selected, locationId);
  }

  formatGiftCard(minor: number): string {
    const { t, store } = this.injected;
    const currency = store.restaurant?.settings.region.currency;
    const decimalDigits =
      typeof currency?.precision === 'number'
        ? currency.precision
        : 2;
    const step = parseFloat(CoreUtils.currency.precision_to_step(decimalDigits) || '0.01');
    const major = parseFloat(((minor || 0) * step).toFixed(6));
    if (typeof t === 'function') return t('currency', { value: major });
    return `${currency?.symbol || ''}${major.toFixed(decimalDigits)}`;
  }

  hasGiftCard(selected: any): boolean {
    return (selected?.giftCard?.balance || 0) > 0 || !!selected?.giftCard?.expiresAt;
  }

  idempotencyKey(kind: string, customerId: string) {
    return `${kind}:${customerId}:${Date.now()}`;
  }

  setActive = async (active: boolean) => {
    if (!this.canAdjust() || !this.state.selected) return;
    const { store } = this.injected;
    const restaurant = store.restaurant!;
    const customerId = this.memberCustomerId(this.state.selected);
    this.setState({ actionError: '' });
    const response = await store.api.native_loyalty_member_active({
      restaurant_id: restaurant._id,
      customer_id: customerId,
      active,
    });
    if (response.outcome) {
      this.setState({ actionError: response.message || 'Could not update member status' });
      return;
    }
    UI.notification.success(active ? 'Member activated' : 'Member deactivated');
    this.openMember(customerId);
    this.search();
  };

  adjustPoints = async () => {
    if (!this.canAdjust() || !this.state.selected) return;
    const { store } = this.injected;
    const restaurant = store.restaurant!;
    const customerId = this.memberCustomerId(this.state.selected);
    const points = parseInt(this.state.points, 10);
    if (!points || points <= 0 || !this.state.reason.trim()) {
      this.setState({ actionError: 'Points and reason are required' });
      return;
    }
    this.setState({ actionError: '' });
    const response = await store.api.native_loyalty_points_adjust({
      restaurant_id: restaurant._id,
      customer_id: customerId,
      action: this.state.action,
      points,
      reason: this.state.reason.trim(),
      idempotency_key: this.idempotencyKey('points', customerId),
    });
    if (response.outcome) {
      this.setState({ actionError: response.message || 'Adjustment failed' });
      return;
    }
    UI.notification.success('Points updated');
    this.setState({ points: '', reason: '' });
    this.openMember(customerId);
  };

  giftCard = async () => {
    if (!this.canAdjust() || !this.state.selected) return;
    const { store } = this.injected;
    const restaurant = store.restaurant!;
    const customerId = this.memberCustomerId(this.state.selected);
    const amount = parseInt(this.state.giftAmount, 10);
    if (!amount || amount <= 0) {
      this.setState({ actionError: 'Gift card amount is required' });
      return;
    }
    const hasCard = this.hasGiftCard(this.state.selected);
    const request = {
      restaurant_id: restaurant._id,
      customer_id: customerId,
      amount,
      reason: this.state.reason.trim() || undefined,
      idempotency_key: this.idempotencyKey(hasCard ? 'recharge' : 'issue', customerId),
    };
    const response = hasCard
      ? await store.api.native_loyalty_gift_card_recharge(request)
      : await store.api.native_loyalty_gift_card_issue(request);
    if (response.outcome) {
      this.setState({ actionError: response.message || 'Gift card action failed' });
      return;
    }
    UI.notification.success(hasCard ? 'Gift card recharged' : 'Gift card issued');
    this.setState({ giftAmount: '' });
    this.openMember(customerId);
  };

  /** Normalises a member row into the columns shown in the design. */
  row(member: any) {
    const name = String(member.name || member.full_name || '').trim();
    const nameParts = name ? name.split(' ') : [];
    const birthDate = member.birth_date || member.birthDate || member.dob;
    const created = member.created_at || member.createdAt;
    const locationId = this.injected.store.restaurant?.settings?.loyalty_providers?.native?.location_id || undefined;
    const points = nativeLoyaltyMemberPoints(member, locationId);
    const status = member.status || (member.active === false ? 'Inactive' : 'Active');
    return {
      id: member.id,
      firstName: member.first_name || member.firstName || nameParts[0] || '—',
      lastName: member.last_name || member.lastName || nameParts.slice(1).join(' ') || '—',
      email: member.email || '—',
      phone: member.phone || '—',
      cardId: member.card_id || member.cardId || member.loyalty_card_id || '—',
      birthDate: birthDate && moment(birthDate).isValid() ? moment(birthDate).format('MM/DD/YYYY') : '—',
      createdAt: created && moment(created).isValid() ? moment(created).format('MM/DD/YYYY') : '—',
      points: points.toLocaleString(),
      status: String(status),
    };
  }

  downloadCsv = () => {
    const rows = this.state.members.map(member => this.row(member));
    if (rows.length === 0) {
      UI.notification.info('There are no members to download');
      return;
    }
    const header = COLUMNS.map(column => column.title).join(',');
    const body = rows
      .map(row => COLUMNS.map(column => `"${String((row as any)[column.key]).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `loyalty-members-${moment().format('YYYY-MM-DD')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  renderToolbarAction(label: string, icon: React.ReactNode, onClick: () => void) {
    return (
      <button
        type="button"
        onClick={onClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: 8,
          border: 'none',
          background: 'transparent',
          color: LC.label,
          fontSize: 16,
          lineHeight: '24px',
          cursor: 'pointer',
        }}
      >
        {icon}
        {label}
      </button>
    );
  }

  renderTable() {
    const { members } = this.state;
    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
          <thead>
            <tr>
              {COLUMNS.map(column => (
                <th
                  key={column.key}
                  style={{
                    width: column.width,
                    padding: '8px 12px',
                    textAlign: column.align || 'left',
                    fontSize: 12,
                    lineHeight: '20px',
                    fontWeight: 500,
                    color: LC.tertiary,
                    whiteSpace: 'nowrap',
                    borderBottom: `1px solid ${LC.metricBorder}`,
                  }}
                >
                  {column.title}
                </th>
              ))}
              <th
                style={{
                  width: 48,
                  borderBottom: `1px solid ${LC.metricBorder}`,
                  padding: '8px 12px',
                  textAlign: 'center' as const,
                  fontSize: 12,
                  color: LC.tertiary,
                  fontWeight: 500,
                }}
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {members.map(member => {
              const row = this.row(member);
              const active = row.status.toLowerCase() === 'active';
              return (
                <tr
                  key={row.id}
                  onClick={() => this.openMember(row.id)}
                  style={{ borderBottom: `1px solid ${LC.rowBorder}`, cursor: 'pointer' }}
                  onMouseEnter={e => (e.currentTarget.style.background = LC.tile)}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  {COLUMNS.map(column => (
                    <td
                      key={column.key}
                      style={{
                        padding: '10px 12px',
                        textAlign: column.align || 'left',
                        fontSize: 13,
                        lineHeight: '20px',
                        color: LC.tertiary,
                        whiteSpace: 'nowrap',
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {column.key === 'status' ? (
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '2px 10px',
                            borderRadius: 12,
                            fontSize: 11,
                            fontWeight: 600,
                            background: active ? '#e6f6ea' : LC.redSoft,
                            color: active ? LC.success : LC.red,
                            border: `1px solid ${active ? '#b7ebc0' : '#ffd0d0'}`,
                          }}
                        >
                          {row.status}
                        </span>
                      ) : (
                        (row as any)[column.key]
                      )}
                    </td>
                  ))}
                  <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                    <button
                      type="button"
                      aria-label="View member details"
                      onClick={e => {
                        e.stopPropagation();
                        this.openMember(row.id);
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 28,
                        height: 28,
                        padding: 0,
                        border: 'none',
                        borderRadius: 6,
                        background: 'transparent',
                        cursor: 'pointer',
                      }}
                    >
                      <FaEllipsisV size={13} color={LC.muted} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  renderPagination() {
    const { page, total, loading } = this.state;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    if (totalPages <= 1 && total === 0) return null;

    const goTo = (p: number) => {
      if (p < 1 || p > totalPages || loading) return;
      this.search(p);
    };

    // Build visible page numbers: always show first 3, last 3, and neighbours of current
    const allPages = Array.from({ length: totalPages }, (_, i) => i + 1);
    const visible = new Set<number>();
    allPages.slice(0, 3).forEach(p => visible.add(p));
    allPages.slice(-3).forEach(p => visible.add(p));
    [page - 1, page, page + 1].forEach(p => { if (p >= 1 && p <= totalPages) visible.add(p); });
    const pageList = allPages.reduce<(number | '...')[]>((acc, p, i, arr) => {
      if (visible.has(p)) {
        if (acc.length > 0 && typeof acc[acc.length - 1] === 'number' && (acc[acc.length - 1] as number) !== p - 1) {
          acc.push('...');
        }
        acc.push(p);
      }
      return acc;
    }, []);

    const btnBase: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      height: 32,
      padding: '0 12px',
      border: `1px solid ${LC.metricBorder}`,
      borderRadius: 8,
      background: LC.white,
      fontSize: 13,
      fontWeight: 500,
      cursor: 'pointer',
      color: LC.label,
    };

    const pageBtn = (p: number | '...', idx: number) => {
      if (p === '...') {
        return <span key={`ellipsis-${idx}`} style={{ fontSize: 13, color: LC.muted, padding: '0 4px' }}>…</span>;
      }
      const active = p === page;
      return (
        <button
          key={p}
          type="button"
          onClick={() => goTo(p)}
          style={{
            ...btnBase,
            background: active ? LC.red : LC.white,
            color: active ? '#fff' : LC.label,
            border: `1px solid ${active ? LC.red : LC.metricBorder}`,
            fontWeight: active ? 600 : 500,
            padding: '0 10px',
          }}
        >
          {p}
        </button>
      );
    };

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 24,
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <button type="button" onClick={() => goTo(page - 1)} disabled={page <= 1} style={{ ...btnBase, opacity: page <= 1 ? 0.4 : 1 }}>
          ← Previous
        </button>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          {pageList.map((p, idx) => pageBtn(p, idx))}
        </div>
        <button type="button" onClick={() => goTo(page + 1)} disabled={page >= totalPages} style={{ ...btnBase, opacity: page >= totalPages ? 0.4 : 1 }}>
          Next →
        </button>
      </div>
    );
  }

  // ─── Figma-style compact input ─────────────────────────────────────────────

  drawerInput(style?: React.CSSProperties): React.CSSProperties {
    return {
      width: '100%',
      height: 32,
      padding: '0 12px',
      fontSize: 12,
      color: '#181d27',
      background: '#fff',
      border: '1px solid #d5d7da',
      borderRadius: 8,
      boxShadow: '0px 1px 1px rgba(10,13,18,0.05)',
      outline: 'none',
      boxSizing: 'border-box' as const,
      ...style,
    };
  }

  // ─── Drawer detail body ─────────────────────────────────────────────────────

  renderDetailBody() {
    const { selected, actionError, detailTab, action, points, reason, giftAmount } = this.state;
    if (!selected) return null;
    const canAdjust = this.canAdjust();
    const hasCard = this.hasGiftCard(selected);
    const customer = selected?.customer || selected;
    const currentPts = this.currentPoints(selected);
    const restaurantName = this.injected.store.restaurant?.name || 'Restaurant';
    const isActive = customer.active !== false;

    const detailRows = [
      { label: 'Name', value: this.memberModalTitle(selected) },
      { label: 'Email', value: customer.email || '—' },
      { label: 'Phone', value: customer.phone || '—' },
      { label: 'Status', value: isActive ? 'Active' : 'Inactive', isStatus: true },
    ];

    const cellLabel: React.CSSProperties = {
      fontSize: 12,
      color: '#535862',
      lineHeight: '18px',
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* ── Member Details ── */}
        <div style={{ paddingBottom: 12, borderBottom: '1px dashed #dfdfdf' }}>
          <p style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 500, color: '#414651' }}>Member Details</p>
          {detailRows.map(row => (
            <div
              key={row.label}
              style={{
                display: 'flex',
                alignItems: 'center',
                height: 32,
                borderBottom: '1px solid #e9eaeb',
              }}
            >
              <div style={{ flex: 1, paddingLeft: 12 }}>
                <span style={cellLabel}>{row.label}</span>
              </div>
              <div style={{ flex: 1, paddingRight: 12, textAlign: 'right' }}>
                {row.isStatus ? (
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '1px 10px',
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 600,
                      background: isActive ? '#e6f6ea' : LC.redSoft,
                      color: isActive ? LC.success : LC.red,
                    }}
                  >
                    {isActive ? 'Active' : 'Inactive'}
                  </span>
                ) : (
                  <span style={cellLabel}>{row.value}</span>
                )}
              </div>
            </div>
          ))}

          {/* Activate / Deactivate */}
          {canAdjust && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <LoyaltyButton onClick={() => this.setActive(!isActive)}>
                {isActive ? 'Deactivate' : 'Activate'}
              </LoyaltyButton>
            </div>
          )}
        </div>

        {/* ── Tabs: Loyalty Points | Gift Card ── */}
        <div style={{ paddingBottom: 12, borderBottom: '1px dashed #dfdfdf' }}>
          {/* Tab nav */}
          <div style={{ display: 'flex', marginBottom: 8 }}>
            {(['points', 'gift_card'] as const).map(tab => {
              const active = detailTab === tab;
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => this.setState({ detailTab: tab })}
                  style={{
                    padding: '6px 16px 6px 0',
                    border: 'none',
                    background: 'transparent',
                    fontSize: 12,
                    fontWeight: 500,
                    color: active ? LC.red : '#535862',
                    cursor: 'pointer',
                    borderBottom: active ? `2px solid ${LC.red}` : '2px solid transparent',
                    marginBottom: -1,
                  }}
                >
                  {tab === 'points' ? 'Loyalty Points' : 'Gift Card'}
                </button>
              );
            })}
          </div>

          {/* Loyalty Points tab */}
          {detailTab === 'points' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <p style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 500, color: '#414651' }}>Points by Location</p>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    height: 32,
                    borderBottom: '1px solid #e9eaeb',
                    marginTop: 8,
                    marginBottom: 8,
                  }}
                >
                  <div style={{ flex: 1, paddingLeft: 12 }}>
                    <span style={cellLabel}>{restaurantName}</span>
                  </div>
                  <div style={{ flex: 1, paddingRight: 12, textAlign: 'right' }}>
                    <span style={cellLabel}>{currentPts.toLocaleString()} pts</span>
                  </div>
                </div>
              </div>

              {canAdjust && (
                <>
                  {/* Adjust Points row */}
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 500, color: '#414651' }}>Adjust Points</p>
                      <input
                        type="number"
                        value={points}
                        placeholder="0"
                        onChange={e => this.setState({ points: e.target.value })}
                        style={this.drawerInput()}
                      />
                    </div>
                    {/* Reload / Deduct toggle */}
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      <button
                        type="button"
                        onClick={() => this.setState({ action: 'add' })}
                        style={{
                          height: 32,
                          padding: '0 12px',
                          border: `2px solid ${action === 'add' ? LC.red : '#d5d7da'}`,
                          borderRadius: 8,
                          background: action === 'add' ? LC.redSoft : '#fff',
                          color: action === 'add' ? LC.red : '#414651',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Reload
                      </button>
                      <button
                        type="button"
                        onClick={() => this.setState({ action: 'subtract' })}
                        style={{
                          height: 32,
                          padding: '0 12px',
                          border: `2px solid ${action === 'subtract' ? LC.red : '#d5d7da'}`,
                          borderRadius: 8,
                          background: action === 'subtract' ? LC.redSoft : '#fff',
                          color: action === 'subtract' ? LC.red : '#414651',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Deduct
                      </button>
                    </div>
                  </div>

                  {/* Reason */}
                  <div>
                    <p style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 500, color: '#414651' }}>Reason</p>
                    <input
                      type="text"
                      value={reason}
                      placeholder="Required"
                      onChange={e => this.setState({ reason: e.target.value })}
                      style={this.drawerInput()}
                    />
                  </div>

                  <LoyaltyButton variant="primary" onClick={this.adjustPoints} style={{ width: '100%' }}>
                    Apply
                  </LoyaltyButton>
                </>
              )}
            </div>
          )}

          {/* Gift Card tab */}
          {detailTab === 'gift_card' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <p style={{ margin: '0 0 16px', fontSize: 12, fontWeight: 500, color: '#414651' }}>
                  {hasCard ? 'Recharge Gift Card' : 'Issue Gift Card'}
                </p>
                <p style={{ margin: '0 0 8px', fontSize: 11, color: '#535862' }}>
                  Gift card balance: {this.formatGiftCard(nativeLoyaltyGiftCardMinor(selected))}
                </p>
                <input
                  type="number"
                  value={giftAmount}
                  placeholder="Amount (minor units)"
                  onChange={e => this.setState({ giftAmount: e.target.value })}
                  style={this.drawerInput()}
                />
                <p style={{ margin: '4px 0 0', fontSize: 11, color: '#535862' }}>
                  Amount is in minor currency units.
                </p>
              </div>
              {canAdjust && (
                <LoyaltyButton variant="primary" onClick={this.giftCard} style={{ width: '100%' }}>
                  {hasCard ? 'Recharge' : 'Issue'}
                </LoyaltyButton>
              )}
            </div>
          )}
        </div>

        {actionError && <FormGroup error={actionError} />}
      </div>
    );
  }

  renderMemberModal() {
    const { detailModalOpen, detailLoading, selected, actionError } = this.state;
    if (!detailModalOpen) return null;

    return (
      <>
        {/* Backdrop */}
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.25)',
            zIndex: 999,
          }}
          onClick={this.closeMemberModal}
        />

        {/* Right-side drawer panel */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            height: '100vh',
            width: 380,
            background: '#fff',
            borderLeft: '1px solid #dee2e6',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
          }}
        >
          {/* Drawer header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              borderBottom: '1px solid #e9eaeb',
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600, color: '#181d27' }}>
              {selected ? this.memberModalTitle(selected) : 'Member'}
            </span>
            <button
              type="button"
              onClick={this.closeMemberModal}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                border: 'none',
                borderRadius: 6,
                background: 'transparent',
                cursor: 'pointer',
                color: '#535862',
              }}
            >
              <FaTimes size={14} />
            </button>
          </div>

          {/* Drawer body */}
          <div style={{ padding: 16, flex: 1 }}>
            {detailLoading && <RotateLoader />}
            {!detailLoading && actionError && !selected && <FormGroup error={actionError} />}
            {!detailLoading && selected && this.renderDetailBody()}
          </div>
        </div>
      </>
    );
  }

  render() {
    const { loading, error, members } = this.state;

    return (
      <LoyaltyShell>
        <LoyaltyCard>
          <LoyaltyCardHeading
            title="Members"
            description="Quaddo members for this merchant — not the CloudWaitress customer list."
          />

          {/* Toolbar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            {/* Search */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flex: '1 0 0',
                minWidth: 0,
                maxWidth: 480,
                background: '#fefefe',
                border: `1px solid ${LC.metricBorder}`,
                borderRadius: 8,
                padding: '6px 12px',
                boxShadow: '0px 1px 1px rgba(10,13,18,0.04)',
              }}
            >
              <FaSearch size={14} color={LC.muted} />
              <input
                type="text"
                value={this.state.query}
                placeholder="Search name, email, phone or card ID..."
                onChange={e => this.setState({ query: e.target.value })}
                onKeyPress={e => {
                  if (e.key === 'Enter') this.search(1);
                }}
                style={{
                  flex: '1 0 0',
                  minWidth: 0,
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  fontSize: 13,
                  lineHeight: '20px',
                  color: LC.label,
                }}
              />
            </div>
            {/* Download */}
            <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
              {this.renderToolbarAction('Download', <FaDownload size={14} color={LC.label} />, this.downloadCsv)}
            </div>
          </div>

          {error && <FormGroup error={error} />}
          {loading && <RotateLoader />}
          {!loading && members.length === 0 && <p style={{ color: LC.muted, margin: 0 }}>No members found.</p>}
          {!loading && members.length > 0 && this.renderTable()}
          {!loading && this.renderPagination()}
        </LoyaltyCard>

        {this.renderMemberModal()}
      </LoyaltyShell>
    );
  }
}
